/**
 * The one live stream (`frontend.md` rule 6). The shell opens `/events` once;
 * a widget never opens its own — v1 had 25 listeners in 25 files.
 *
 * - `data_changed` refetches the queries of its area, by key: every query key
 *   begins with the area's name (`["todos", "lists"]`), which is the name the
 *   server announces. An area the offline cache holds is synced as well, so
 *   its cached rows catch up.
 * - `sync_complete` refetches what a sync fills.
 * - `connected` after a drop refetches everything and syncs: whatever was
 *   announced while the stream was down was missed (v1's reconnect belt).
 * - A stream the browser has closed for good is opened again (#49).
 * - A screen back from the background after `AWAY_MS` opens a new stream (#100):
 *   one held while a phone slept, or by a proxy, can die with no `error`, and
 *   the reply that came meanwhile was never heard.
 *
 * Anything else that wants an event — a banner, chat in 26B — listens through
 * `onLive`, on the same stream.
 */
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import type { LiveEventName, LiveEvents } from "../api/client";

/** Every event the stream carries. The compiler holds it to the contract. */
const NAMES = [
  "agent_error",
  "agent_step",
  "auth_invalid",
  "briefing_ready",
  "canvas_updated",
  "confirmation_required",
  "confirmation_resolved",
  "connected",
  "data_changed",
  "finance_alert",
  "job_updated",
  "message",
  "message_part",
  "run_finished",
  "run_stopped",
  "skill_run_complete",
  "sync_complete",
  "sync_error",
  "sync_started",
] as const satisfies readonly LiveEventName[];
type Exact<A, B> = [A] extends [B] ? ([B] extends [A] ? true : never) : never;
export const namesCoverEveryEvent: Exact<(typeof NAMES)[number], LiveEventName> = true;

/** The areas whose rows the offline cache holds (25C): a change means a sync. */
export const SYNCED_AREAS = new Set([
  "todos",
  "journal",
  "notes",
  "recipes",
  "concerns",
  "turner",
  "atlas",
  "stretching",
  "finance",
]);

/** What a sync job fills. */
const SYNC_FILLS = ["health", "calendar", "turner"];

type Handler<N extends LiveEventName> = (data: LiveEvents[N]) => void;
const handlers = new Map<LiveEventName, Set<Handler<LiveEventName>>>();

/** Hear one kind of event. Returns the function that stops hearing it. */
export function onLive<N extends LiveEventName>(name: N, handler: Handler<N>): () => void {
  const set = handlers.get(name) ?? new Set();
  set.add(handler as Handler<LiveEventName>);
  handlers.set(name, set);
  return () => set.delete(handler as Handler<LiveEventName>);
}

function emit<N extends LiveEventName>(name: N, data: LiveEvents[N]): void {
  for (const handler of handlers.get(name) ?? []) handler(data);
}

export interface LiveDeps {
  client: QueryClient;
  /** Ask the offline cache to catch up (`offline/register.ts`'s `syncNow`). */
  sync: () => void;
  open?: (url: string) => EventSource;
}

/**
 * A stream the browser has closed is opened again after this. The browser
 * itself retries a stream that dropped, but not one whose retry was refused
 * (a 502 while the server restarts, say): that one stays closed, and the screen
 * heard no more answers (#49).
 */
export const REOPEN_MS = 5_000;

/**
 * Away this long, a screen opens a new stream when it is seen again (#100): a
 * PWA in the background showed no reply that its phone had. A stream that
 * died there sent no `error`, so nothing opened it again.
 */
export const AWAY_MS = 10_000;

/** `EventSource.CLOSED`, which a test's fake stream does not carry. */
const CLOSED = 2;

/** Open the stream. Returns the function that closes it. */
export function startLive({
  client,
  sync,
  open = (url) => new EventSource(url),
}: LiveDeps): () => void {
  let connections = 0;
  const own: (() => void)[] = [
    onLive("data_changed", ({ domain }) => {
      void client.invalidateQueries({ queryKey: [domain] });
      if (SYNCED_AREAS.has(domain)) sync();
    }),
    onLive("sync_complete", () => {
      for (const area of SYNC_FILLS) void client.invalidateQueries({ queryKey: [area] });
      sync();
    }),
    onLive("connected", () => {
      connections += 1;
      if (connections === 1) return;
      void client.invalidateQueries();
      sync();
    }),
  ];
  const listeners = NAMES.map((name) => {
    const listener = (event: MessageEvent<string>) => {
      let data: unknown = {};
      try {
        data = event.data ? JSON.parse(event.data) : {};
      } catch {
        return;
      }
      emit(name, data as LiveEvents[typeof name]);
    };
    return [name, listener as EventListener] as const;
  });
  let stopped = false;
  let reopen: ReturnType<typeof setTimeout> | undefined;
  let stream = attach();
  let hiddenAt: number | null = null;
  document.addEventListener("visibilitychange", seen);

  function attach(): EventSource {
    const next = open("/events");
    for (const [name, listener] of listeners) next.addEventListener(name, listener);
    next.addEventListener("error", failed);
    return next;
  }
  function detach(): void {
    for (const [name, listener] of listeners) stream.removeEventListener(name, listener);
    stream.removeEventListener("error", failed);
    stream.close();
  }
  function failed(): void {
    if (stopped || stream.readyState !== CLOSED) return;
    clearTimeout(reopen);
    reopen = setTimeout(() => {
      if (stopped) return;
      detach();
      // Its `connected` is a reconnection: everything missed is read again.
      stream = attach();
    }, REOPEN_MS);
  }

  function seen(): void {
    if (document.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    const away = hiddenAt === null ? 0 : Date.now() - hiddenAt;
    hiddenAt = null;
    if (stopped || away < AWAY_MS) return;
    clearTimeout(reopen);
    detach();
    // Its `connected` is a reconnection: everything missed is read again.
    stream = attach();
  }

  return () => {
    stopped = true;
    clearTimeout(reopen);
    document.removeEventListener("visibilitychange", seen);
    detach();
    for (const stop of own) stop();
  };
}

/** `onLive`, for a component: heard while it is mounted. */
export function useLive<N extends LiveEventName>(name: N, handler: Handler<N>): void {
  const latest = useRef(handler);
  useEffect(() => {
    latest.current = handler;
  });
  useEffect(() => onLive(name, (data) => latest.current(data)), [name]);
}
